// src/pages/invoicing/create-tax-from-pi.js
import React, { useEffect, useState } from "react";
import Header from "../../components/Header";
import { Link } from "react-router-dom";
import API_BASE_URL from "../../config";
import DataTable from "react-data-table-component";

const CreateTaxFromPI = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Default month range
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
    .toISOString()
    .split("T")[0];
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0)
    .toISOString()
    .split("T")[0];

  const [fromDate, setFromDate] = useState(firstDay);
  const [toDate, setToDate] = useState(lastDay);
  const [fromDateType, setFromDateType] = useState("text");
  const [toDateType, setToDateType] = useState("text");

  // Brand id -> name map (for listing)
  const [brandsMap, setBrandsMap] = useState({});

  useEffect(() => {
    document.title = "Create Tax Invoice from PI | Webyappy";
  }, []);

  // Load Brands (for Brand column)
  useEffect(() => {
    fetch(`${API_BASE_URL}/invoicing/get_brands`, { credentials: "include" })
      .then((r) => r.json())
      .then((d) => {
        if (d.status === "success") {
          const m = {};
          (d.brands || []).forEach((b) => {
            m[b.id] = b.brand_name;
          });
          setBrandsMap(m);
        }
      })
      .catch(() => {});
  }, []);

  // Load PIs
  useEffect(() => {
    setLoading(true);
    fetch(
      `${API_BASE_URL}/invoicing/get_all_proformas?start_date=${fromDate}&end_date=${toDate}`,
      { credentials: "include" }
    )
      .then((r) => r.json())
      .then((d) => {
        if (d.status === "success") setRows(d.proformas || []);
      })
      .finally(() => setLoading(false));
  }, [fromDate, toDate]);

  const columns = [
    { name: "PI No", selector: (r) => r.pi_no, sortable: true },
    { name: "Company", selector: (r) => r.to_company || "-", sortable: true },
    {
      name: "Brand",
      selector: (row) => brandsMap[row.brand_id] || "-",
      sortable: true,
    },
    {
      name: "Amount",
      selector: (row) =>
        `₹${Math.round(parseFloat(row.grand_total || 0)).toLocaleString("en-IN")}`,
      sortable: true,
      right: true,
    },
    {
      name: "Generated On",
      selector: (r) =>
        new Date(r.created_on).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
      sortable: true,
    },
    {
      name: "Generated By",
      selector: (r) => r.created_by || "-",
      sortable: true,
    },
    {
      name: "Action",
      cell: (r) => (
        <>
          {/* View PI */}
          <a
            href={`/pages/invoicing/pi-view/${r.id}`}
            target="_blank"
            rel="noopener noreferrer"
            title="View PI"
            className="mr-3"
            style={{ marginRight: "10px" }}
          >
            <i className="far fa-eye text-danger icon-lg"></i>
          </a>

          {/* Proceed to Create TI from this PI */}
          <Link
            to={`/pages/invoicing/create-ti-from-pi-items/${r.id}`}
            title="Create Tax Invoice from this PI"
          >
            <i className="fas fa-arrow-right icon-lg text-danger"></i>
          </Link>
        </>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  const filteredRows = rows.filter((r) => {
    const pi = (r.pi_no || "").toLowerCase();
    const comp = (r.to_company || "").toLowerCase();
    const brand = (brandsMap[r.brand_id] || "").toLowerCase();
    const q = searchTerm.toLowerCase();
    return pi.includes(q) || comp.includes(q) || brand.includes(q);
  });

  return (
    <>
      <Header />
      <div style={{ paddingTop: "70px", paddingBottom: "70px" }}>
        <div className="container-fluid px-3 py-3">
          {/* Breadcrumb + Back */}
          <div className="d-flex align-items-center justify-content-between mb-4">
            <ul className="breadcrumb breadcrumb-transparent breadcrumb-dot font-weight-bold mb-0 p-0">
              <li className="breadcrumb-item">
                <Link to="/home" className="text-muted">Home</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/pages/invoicing/Invoicing" className="text-muted">
                  Invoicing
                </Link>
              </li>
              <li className="breadcrumb-item">
                <span className="text-dark font-weight-bold">
                  Create Tax Invoice from PI
                </span>
              </li>
            </ul>
            <Link to="/pages/invoicing/Invoicing" className="btn btn-light">
              <i className="la la-arrow-left"></i> Back
            </Link>
          </div>

          {/* Card */}
          <div className="card card-custom shadow-sm">
            <div className="card-body">
              {/* Filters */}
              <div className="row mb-4">
                <div className="col-md-3">
                  <label className="font-weight-bold text-dark">From Date</label>
                  <input
                    type={fromDateType}
                    className="form-control"
                    value={
                      fromDateType === "date"
                        ? fromDate
                        : new Date(fromDate).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                    }
                    onFocus={() => setFromDateType("date")}
                    onBlur={() => setFromDateType("text")}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <label className="font-weight-bold text-dark">To Date</label>
                  <input
                    type={toDateType}
                    className="form-control"
                    value={
                      toDateType === "date"
                        ? toDate
                        : new Date(toDate).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                    }
                    onFocus={() => setToDateType("date")}
                    onBlur={() => setToDateType("text")}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <label className="font-weight-bold text-dark">Search</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by PI No, Company, or Brand"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              <DataTable
                columns={columns}
                data={filteredRows}
                progressPending={loading}
                pagination
                highlightOnHover
                responsive
                striped
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateTaxFromPI;
